import React, { useEffect, useMemo, useState } from "react";
import type BusinessUser from "@tootsweet/model/entities/BusinessUser";
import {
  updateCustomerThunk,
  useAccountToDisplaySelector
} from "../../../../redux/actions/user";
import { KC_Sizes } from "../../../../designsystem/ds_commons";
import { useDispatch } from "react-redux";
import OrderSummary from "./OrderSummary";
import "./styles.scss";
import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";
import { LANGUAGE, tt } from "../../../../utils/i18n/i18n";
import KC_Heading1 from "../../../../designsystem/typography/KC-Heading/KC_Heading1";
import CheckoutFormOuter from "../../../../components/CheckoutForm/CheckoutFormOuter";
import {
  setCurrency,
  setError,
  setLoading,
  useSubscriptionPlansSelector
} from "../../../../redux/actions/app";
import moment from "moment-timezone";
import NiceModal from "@ebay/nice-modal-react";
import { JETPACK_CONFIG, ONBOARDING } from "../../../../utils/routes/routes";
import { buildPathWithTrackingId } from "../../../../utils/routing/routingUtils";
import { type SaasCommitment } from "../../../../model/SaasTypes";
import PostPaymentModal from "../../../../components/modal/ConfirmPayment/PostPaymentModal";
import CodePromo from "../../../../components/CodePromo/CodePromo";
import { useHistory } from "react-router-dom";
import {
  COLLECT_TVA_COUNTRY,
  DEFAULT_PLAN_OPTION
} from "../../../../utils/constants";
import {
  ComputePricesResponse,
  fetchPrices,
  fetchPromoCode
} from "../../../../components/CodePromo/api";
import SaaSPromoCode from "@tootsweet/model/entities/saas/SaaSPromoCode";
import { getCurrencyFromCountryCode } from "@tootsweet/model/enums/Currencies";
import KC_Separator from "../../../../designsystem/KC_Separator/KC_Separator";
import SubscriptionPromoCode2 from "@tootsweet/model/entities/SubscriptionPromoCode2";
import { fetchSubscriptionPromoCode2 } from "../../../../api/fetchSubscriptionPromoCode2";
import SubscriptionPlanDTO, {
  SubscriptionPlanType
} from "@tootsweet/model/datatransfer/SubscriptionPlanDTO";
import CheckoutFormBillingInformation from "./CheckoutFormBillingInformation";
import getErrorMessage from "../../../../utils/error_handling/getErrorMessage";
import Axios from "axios";
import CONFIG from "../../../../model/TSConfig";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";

export interface JetpackCheckoutParams {
  isNew?: boolean;
  simpleOnboarding?: boolean;
  placeName?: string | null;
  planId?: string | null;
  duration?: string | null;
  commitment?: string | null;
  spc?: string | null;
  sts?: string | null;
  useSamePayment?: string | null;
  copyFromAccount?: string | null;
  payment_intent?: string | null;
  payment_intent_client_secret?: string | null;
  redirect_status?: string | null;
  setup_intent?: string | null;
  setup_intent_client_secret?: string | null;
}

interface Props {
  isSubscriptionPromoCode2?: boolean;
  redirectToOnboardingAfterSubscription?: boolean;
  className?: string;
  params: JetpackCheckoutParams;
}

const JetpackCheckoutForm = (props: Props) => {
  const dispatch = useDispatch();

  const { accountToDisplay } = useAccountToDisplaySelector();

  const allSubscriptionPlans = useSubscriptionPlansSelector();
  const history = useHistory();

  const [hidePaymentForm, setHidePaymentForm] = useState(false);

  const [computePricesResponse, setComputePricesResponse] =
    useState<ComputePricesResponse>({
      currentSub: "",
      currentSubPrice: 0,
      discountPcBase100: 0,
      nextSub: "",
      nextSubPriceBeforeDiscount: 0,
      nextSubPriceExclVAT: 0,
      nextSubPriceVAT: 0,
      nextSubPriceInclVAT: 0,
      upsellExclVAT: 0,
      upsellVAT: 0,
      upsellInclVAT: 0
    });

  const [saasPromoCode, setSaasPromoCode] = useState<SaaSPromoCode | null>(
    null
  );

  const [subscriptionPromoCode2, setSubscriptionPromoCode2] =
    useState<SubscriptionPromoCode2 | null>(null);

  const [blockCustomerForm, setBlockCustomerForm] = useState(false);
  const [showBlurOverlay, setShowBlurOverlay] = useState(false);

  const handleDateChange = (date: Date) => {
    if (isUpsell) {
      return;
    }
    const dateTs = moment(date).tz("UTC", true).startOf("day").unix();
    const search = new URLSearchParams(window.location.search);
    search.set("sts", dateTs.toString());
    history.replace({ search: search.toString() });
  };

  const fetchSubPromoCode2 = async () => {
    if (
      !accountToDisplay?.token ||
      !accountToDisplay?.businessUser?.tempsSubscriptionPromoCode2
    ) {
      return;
    }

    try {
      const res = await fetchSubscriptionPromoCode2(
        accountToDisplay?.businessUser?.tempsSubscriptionPromoCode2,
        accountToDisplay?.token
      );

      setSubscriptionPromoCode2(res);
    } catch (e) {
      dispatch(setError(tt("jetpack_checkout_form_error_no_promo_code")));
    }
  };

  const refreshPrices = async () => {
    try {
      if (
        !accountToDisplay?.token ||
        !accountToDisplay?.trackingID ||
        !saasCommitment ||
        !props.params.planId
      ) {
        return;
      }

      dispatch(setLoading(true));

      const promoCode2 =
        accountToDisplay?.businessUser?.tempsSubscriptionPromoCode2 ?? "";

      if (!promoCode2 && !subscriptionPlanId) {
        return;
      }

      let promoCodeIdToUse = saasPromoCode?._id;

      if (spcParam && spcParam !== saasPromoCode?._id) {
        try {
          const promoCode = await fetchPromoCode(
            accountToDisplay?.trackingID,
            spcParam,
            props.params.planId,
            accountToDisplay?.token
          );
          if (promoCode) {
            promoCodeIdToUse = promoCode._id;
            setSaasPromoCode(promoCode);
          }
        } catch (e) {
          // @ts-ignore
          if (e?.response?.data) {
            // @ts-ignore
            dispatch(setError(getErrorMessage(e?.response?.data?.code)));
          } else {
            console.error("Failed to fetch promo code from URL:", e);
            dispatch(setError(tt("error_promo_code_not_exist")));
          }

          promoCodeIdToUse = "";
          setSaasPromoCode(null);
        }
      } else if (!spcParam) {
        promoCodeIdToUse = "";
        setSaasPromoCode(null);
      }

      let newPriceResponse: ComputePricesResponse;

      newPriceResponse = await fetchPrices(
        accountToDisplay?.token,
        subscriptionPlanId,
        saasCommitment,
        promoCodeIdToUse || undefined,
        promoCode2
      );

      if (newPriceResponse.upsellExclVAT < 0) {
        history.push(
          buildPathWithTrackingId(JETPACK_CONFIG, accountToDisplay?.trackingID)
        );
      }

      setComputePricesResponse(newPriceResponse);

      if (newPriceResponse.currency) {
        dispatch(setCurrency(newPriceResponse.currency));
      }
    } catch (err) {
      console.error(err);
      alert(tt("jetpack_checkout_form_error_price_calculation"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const activateFreeLicense = async () => {
    if (!accountToDisplay?.token || !saasPromoCode?._id) {
      return;
    }

    try {
      dispatch(setLoading(true));

      const response = await Axios({
        method: "POST",
        url: `${CONFIG.saasBackendUrl}/subscription/activate-free-license`,
        headers: {
          Authorization: `Bearer ${accountToDisplay.token}`
        },
        data: {
          promoCodeId: saasPromoCode._id
        }
      });

      if (response.data?.success) {
        dispatch(
          updateCustomerThunk(
            {
              isOnboarded: !subscriptionPromoCode2,
              isSaasFreeLicense: true
            },
            accountToDisplay?.trackingID,
            accountToDisplay?.token
          )
        );

        await openPostPaymentModal();
        setHidePaymentForm(true);
      }
    } catch (err) {
      console.error("Error activating free license:", err);
      dispatch(setError(tt("jetpack_checkout_form_error_price_calculation")));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const subscribeWithNoCard = async () => {
    if (
      !accountToDisplay ||
      (!subscriptionPromoCode2 && !subscriptionPlanId) ||
      !saasCommitment ||
      !accountToDisplay?.token
    ) {
      return;
    }

    try {
      dispatch(setLoading(true));

      await Axios({
        method: "POST",
        url: `${CONFIG.saasBackendUrl}/subscription`,
        headers: {
          Authorization: `Bearer ${accountToDisplay.token}`
        },
        data: {
          businessUserId: accountToDisplay.email,
          saasPlanId: subscriptionPlanId,
          saasCommitment,
          payment_intent_id: null,
          startTs,
          promoCode: saasPromoCode?._id,
          promoCode2:
            accountToDisplay?.businessUser?.tempsSubscriptionPromoCode2 ?? "",
          setup_intent_id: null
        }
      });

      const isOnboarded = !subscriptionPromoCode2;
      dispatch(
        updateCustomerThunk(
          { isOnboarded },
          accountToDisplay?.trackingID,
          accountToDisplay?.token
        )
      );

      await openPostPaymentModal();
      setHidePaymentForm(true);
    } catch (err) {
      console.error("Error subscribing with noCard:", err);

      let errorMsg = tt("jetpack_checkout_form_error_price_calculation");
      const anyErr = err as any;

      if (anyErr.response?.data?.code) {
        errorMsg = getErrorMessage(anyErr.response.data.code);
      }

      dispatch(setError(errorMsg));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePaymentSuccess = async () => {
    if (
      !accountToDisplay ||
      (!subscriptionPromoCode2 && !subscriptionPlanId) ||
      !saasCommitment ||
      !accountToDisplay?.token
    ) {
      return;
    }

    // Original flow for existing accounts
    const isOnboarded = !subscriptionPromoCode2;
    dispatch(
      updateCustomerThunk(
        { isOnboarded },
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
    dispatch(setLoading(false));

    await openPostPaymentModal();
    setHidePaymentForm(true);
  };

  const openPostPaymentModal = async () => {
    setShowBlurOverlay(true);

    const confirmed = await NiceModal.show(PostPaymentModal, {
      headerText: tt("jetpack_checkout_form_success_header"),
      bodyText: tt("jetpack_checkout_form_success_body"),
      buttonText: tt("jetpack_checkout_form_success_button", {
        planName:
          saasPlan?.longNameL?.[LANGUAGE] ??
          saasPlan?.nameL?.[LANGUAGE] ??
          saasPlan?.name
      })
    });

    let update: Partial<BusinessUser> = {
      isOnboarded: !accountToDisplay?.businessUser?.tempsSubscriptionPromoCode2
    };

    if (confirmed) {
      // If isNew is true, redirect to onboarding after payment
      if (props.params.isNew) {
        const onboardingPath = buildPathWithTrackingId(
          ONBOARDING,
          accountToDisplay?.trackingID,
          false,
          props.params.simpleOnboarding
            ? {
                so: "true"
              }
            : undefined
        );

        history.push(onboardingPath);
        return;
      }

      if (
        accountToDisplay?.businessUser?.tempsSubscriptionPromoCode2 ||
        props.redirectToOnboardingAfterSubscription
      ) {
        update.tempsSubscriptionPromoCode2 = null;
        history.push(
          buildPathWithTrackingId(ONBOARDING, accountToDisplay?.trackingID)
        );
      } else {
        history.push(
          buildPathWithTrackingId(JETPACK_CONFIG, accountToDisplay?.trackingID)
        );
      }
      window.location.reload();
    }

    setShowBlurOverlay(false);
  };

  function renderPromoCode(className: string, idSuffix: string) {
    return (
      <div
        className={`promo-code-container-container ${className} mb-2`}
        style={{ gridColumn: 2, gridRow: 2 }}
      >
        <div className="p-4 bg-white" style={{ borderRadius: 6 }}>
          <KC_Heading3 text={tt("jetpack_checkout_form_promo_code")} />
          <CodePromo
            saasPlanId={subscriptionPlanId || ""}
            saasCommitment={saasCommitment || ""}
            subscriptionPromoCode2={
              accountToDisplay?.businessUser?.tempsSubscriptionPromoCode2
            }
            appliedPromoCodeText={saasPromoCode?._id}
            idSuffix={idSuffix}
          />
        </div>
      </div>
    );
  }

  const subscriptionPlanId = useMemo<string>(() => {
    return props.params.planId || "";
  }, [props.params.planId]);

  const isUpsell = useMemo(() => {
    return (
      !!computePricesResponse?.currentSubPrice &&
      computePricesResponse.currentSubPrice > 0
    );
  }, [computePricesResponse]);

  const isFirstMonthFree = useMemo(() => {
    return !!saasPromoCode?.firstMonthFree;
  }, [saasPromoCode]);

  const isFreeLicense = useMemo(() => {
    return !!saasPromoCode?.isFree;
  }, [saasPromoCode]);

  const isNoCard = useMemo(() => {
    return !!saasPromoCode?.noCard;
  }, [saasPromoCode]);

  const displayDiscountCode = useMemo(() => {
    if (props.isSubscriptionPromoCode2) {
      return false;
    }

    if (computePricesResponse?.currentSub) {
      return false;
    }

    return true;
  }, [props.isSubscriptionPromoCode2, computePricesResponse]);

  const spcParam = useMemo(() => {
    return props.params.spc;
  }, [props.params.spc]);

  const saasCommitment = useMemo<SaasCommitment>(() => {
    if (props.isSubscriptionPromoCode2) {
      if (!subscriptionPromoCode2 || !subscriptionPromoCode2.places) {
        return "monthly";
      }
      return subscriptionPromoCode2?.places?.[0]?.period === 1
        ? "monthly"
        : "yearly";
    }
    const durationFromURLQuery = (props.params.duration ||
      props.params.commitment) as SaasCommitment;

    return durationFromURLQuery ?? "monthly";
  }, [props.params.duration, props.params.commitment, subscriptionPromoCode2]);

  const startTs = useMemo(() => {
    const stsParam = props.params.sts;
    if (stsParam) {
      const timestamp = parseInt(stsParam, 10);
      if (!isNaN(timestamp)) {
        return timestamp;
      }
    }

    // Encode today's wall date as UTC midnight, matching the date-picker path
    // (handleDateChange). Without startOf("day") the current time-of-day is
    // carried along, and once converted to the user's timezone on the backend
    // it can roll over to the next day, wrongly flagging "today" as future.
    return moment().tz("UTC", true).startOf("day").unix();
  }, [props.params.sts]);

  const activeDate = useMemo<Date>(() => {
    return moment.unix(startTs).toDate();
  }, [startTs]);

  const firstPaymentDate = useMemo<Date>(() => {
    if (isFirstMonthFree) {
      return moment.unix(startTs).add(1, "month").toDate();
    }
    return moment.unix(startTs).toDate();
  }, [startTs, isFirstMonthFree]);

  const renewalTs = useMemo(() => {
    if (subscriptionPromoCode2 && subscriptionPromoCode2.places?.length) {
      return (
        moment(startTs, "X")
          // .tz("UTC", true)
          .add(subscriptionPromoCode2.places[0].commitment || 1, "month")
          .startOf("day")
          .unix()
      );
    }

    if (saasPromoCode?.firstMonthFree) {
      return (
        moment(startTs, "X")
          // .tz("UTC", true)
          .add(1, saasCommitment === DEFAULT_PLAN_OPTION ? "year" : "month")
          .add(1, "month")
          .startOf("day")
          .unix()
      );
    }

    return (
      moment(startTs, "X")
        // .tz("UTC", true)
        .add(1, saasCommitment === DEFAULT_PLAN_OPTION ? "year" : "month")
        .startOf("day")
        .unix()
    );
  }, [startTs, saasCommitment, subscriptionPromoCode2, saasPromoCode]);

  const mustPayVAT = useMemo(() => {
    return !!(
      accountToDisplay?.businessUser?.billingCountry &&
      accountToDisplay?.businessUser.billingCountry.toLowerCase() ===
        COLLECT_TVA_COUNTRY.toLowerCase()
    );
  }, [accountToDisplay]);

  const saasPlan = useMemo(() => {
    if (props.isSubscriptionPromoCode2) {
      return buildPlanFromSubscriptionPromoCode2(
        subscriptionPromoCode2,
        accountToDisplay?.businessUser
      );
    }
    return allSubscriptionPlans?.find(
      (sub) => sub._id.toString() === subscriptionPlanId
    );
  }, [
    subscriptionPlanId,
    allSubscriptionPlans,
    subscriptionPromoCode2,
    accountToDisplay?.trackingID
  ]);

  const displayCheckoutForm = useMemo(() => {
    return (
      !!computePricesResponse?.upsellExclVAT && !!accountToDisplay?.trackingID
    );
  }, [computePricesResponse, accountToDisplay]);

  const isStartDateInFuture = useMemo(() => {
    // Get today's date in user's timezone (wall date)
    const todayInUserTz = moment().tz("UTC").startOf("day");

    // Get start date in user's timezone (wall date)
    const startDateInUserTz = moment
      .unix(startTs)
      .tz("UTC", true)
      .startOf("day");

    // Return true if start date is after today
    return startDateInUserTz.isAfter(todayInUserTz);
  }, [startTs, accountToDisplay?.businessUser?.tz]);

  useEffect(() => {
    void refreshPrices();
  }, [subscriptionPlanId, saasCommitment, accountToDisplay, spcParam]);

  useEffect(() => {
    if (
      props.params.payment_intent &&
      props.params.payment_intent_client_secret &&
      props.params.redirect_status
    ) {
      setBlockCustomerForm(true);
      setHidePaymentForm(false);
      return;
    }

    if (
      props.params.setup_intent &&
      props.params.setup_intent_client_secret &&
      props.params.redirect_status
    ) {
      setBlockCustomerForm(true);
      setHidePaymentForm(false);
    }
  }, [
    props.params.payment_intent,
    props.params.payment_intent_client_secret,
    props.params.redirect_status,
    props.params.setup_intent,
    props.params.setup_intent_client_secret
  ]);

  useEffect(() => {
    if (
      props.isSubscriptionPromoCode2 &&
      accountToDisplay?.businessUser?.tempsSubscriptionPromoCode2
    ) {
      void fetchSubPromoCode2();
    }
  }, [
    props.isSubscriptionPromoCode2,
    accountToDisplay?.businessUser?.tempsSubscriptionPromoCode2
  ]);

  useEffect(() => {
    const billingCountry = accountToDisplay?.businessUser?.billingCountry;
    if (!billingCountry || isUpsell) {
      return;
    }
    dispatch(setCurrency(getCurrencyFromCountryCode(billingCountry)));
  }, [accountToDisplay?.businessUser?.billingCountry, isUpsell]);

  return (
    <div
      id="checkout-main-container"
      className={`h-100 p-4 d-flex flex-column checkout-main-container ${props.className || ""}`}
      style={{
        overflowY: "scroll",
        position: "relative"
      }}
    >
      {showBlurOverlay && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            zIndex: 100,
            pointerEvents: "none"
          }}
        />
      )}
      <div>
        <KC_Heading1
          text={
            props.params.isNew ? (
              <>
                {tt("new_venue_add_1")}{" "}
                <span className="text-red-secondary">
                  {tt("new_venue_add_2")}
                </span>
              </>
            ) : (
              <>
                {tt("jetpack_checkout_form_become_member")}{" "}
                <span className="text-red-secondary">
                  {tt("jetpack_checkout_form_club")}
                </span>
              </>
            )
          }
          className="text-blue mb-3"
        />
      </div>
      <div
        className="checkout-info-form-container"
        style={{
          display: "grid",
          gridTemplateColumns: "60% 40%",
          gridAutoRows: "auto"
        }}
      >
        <div
          className="d-block d-sm-none mb-2"
          style={{ gridColumn: 2, gridRow: 1 }}
        >
          {saasPlan != null && saasCommitment && (
            <OrderSummary
              plan={saasPlan}
              duration={saasCommitment}
              startTs={startTs}
              renewalTs={renewalTs}
              mustPayVAT={mustPayVAT}
              computePricesResponse={computePricesResponse}
              isFirstMonthFree={isFirstMonthFree}
              firstPaymentDate={firstPaymentDate}
              isFreeLicense={isFreeLicense}
            />
          )}
        </div>
        <div
          className="d-flex flex-column st-gap p-4 checkout-info-form mb-2"
          style={{ gridColumn: 1, gridRow: 1 }}
        >
          <CheckoutFormBillingInformation
            blockCustomerForm={blockCustomerForm}
            setBlockCustomerForm={setBlockCustomerForm}
            activeDate={activeDate}
            onDateChange={handleDateChange}
            isUpsell={isUpsell}
            isNew={props.params.isNew}
          />
        </div>
        <div
          className="d-none d-sm-block mb-2"
          style={{ gridColumn: 2, gridRow: 1 }}
        >
          {saasPlan != null && saasCommitment && (
            <OrderSummary
              plan={saasPlan}
              duration={saasCommitment}
              startTs={startTs}
              renewalTs={renewalTs}
              mustPayVAT={mustPayVAT}
              computePricesResponse={computePricesResponse}
              isFirstMonthFree={isFirstMonthFree}
              firstPaymentDate={firstPaymentDate}
              isFreeLicense={isFreeLicense}
            />
          )}
        </div>
      </div>
      {blockCustomerForm && !hidePaymentForm && (
        <>
          <div
            className="checkout-info-form-container"
            style={{
              display: "grid",
              gridTemplateColumns: "60% 40%",
              gridAutoRows: "auto"
            }}
          >
            {displayDiscountCode &&
              renderPromoCode("d-grid d-sm-none", "mobile")}
            <div
              id="checkout-info-form"
              className="p-4 checkout-info-form mb-2"
              style={{ gridColumn: 1, gridRow: 2 }}
            >
              {isFreeLicense ? (
                <div className="d-flex flex-column w-100 h-100">
                  <KC_Heading3 text={tt("free_license_title")} />
                  <p className="mt-4">
                    {tt("free_license_description", {
                      planName: saasPlan?.name
                    })}
                  </p>
                  <div className="flex-grow-1">&nbsp;</div>
                  <div className="w-100 d-none d-md-block mt-4">
                    <KC_Button
                      className="w-50 ml-auto"
                      onClick={activateFreeLicense}
                      size={KC_ButtonSize.Large}
                      isPrimary={true}
                      text={tt("free_license_confirm_button")}
                    />
                  </div>
                  <div className="d-block d-md-none mt-4">
                    <KC_Button
                      onClick={activateFreeLicense}
                      size={KC_ButtonSize.Large}
                      isPrimary={true}
                      text={tt("free_license_confirm_button")}
                    />
                  </div>
                </div>
              ) : isNoCard ? (
                <div className="d-flex flex-column w-100 h-100">
                  <KC_Heading3
                    text={tt("jetpack_checkout_form_payment_method")}
                  />
                  <p className="mt-4">
                    {tt("no_card_subscription_description", {
                      planName: saasPlan?.name
                    })}
                  </p>
                  <p className="mt-2">
                    {tt("no_card_subscription_description_2", {
                      planName: saasPlan?.name
                    })}
                  </p>
                  <div className="flex-grow-1">&nbsp;</div>
                  <div className="w-100 d-none d-md-block mt-4">
                    <KC_Button
                      className="w-50 ml-auto"
                      onClick={subscribeWithNoCard}
                      size={KC_ButtonSize.Large}
                      isPrimary={true}
                      text={tt("confirm_subscription")}
                    />
                  </div>
                  <div className="d-block d-md-none mt-4">
                    <KC_Button
                      onClick={subscribeWithNoCard}
                      size={KC_ButtonSize.Large}
                      isPrimary={true}
                      text={tt("confirm_subscription")}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <KC_Heading3
                    text={tt("jetpack_checkout_form_payment_method")}
                  />
                  <KC_Separator size={KC_Sizes.sm} />
                  <KC_Separator size={KC_Sizes.sm} />
                  {displayCheckoutForm && (
                    <CheckoutFormOuter
                      // @ts-ignore
                      businessUserId={accountToDisplay?.email}
                      bill={null}
                      amount={computePricesResponse.upsellInclVAT}
                      posaDeliveryIds={[""]}
                      onFinished={handlePaymentSuccess}
                      doNotRedirect={false}
                      isSaasSubscriptionForm={true}
                      saasCommitment={saasCommitment}
                      saasPlanId={subscriptionPlanId}
                      promoCode={saasPromoCode?._id}
                      promoCode2={
                        accountToDisplay?.businessUser
                          ?.tempsSubscriptionPromoCode2 ?? ""
                      }
                      startTs={startTs}
                      onPayStarted={() => dispatch(setLoading(true))}
                      isFirstMonthFree={isFirstMonthFree}
                      firstPaymentDate={firstPaymentDate}
                      isStartDateInFuture={isStartDateInFuture}
                    />
                  )}
                </>
              )}
            </div>
            {displayDiscountCode &&
              renderPromoCode("d-none d-sm-block", "desktop")}
          </div>
        </>
      )}
    </div>
  );
};

export default JetpackCheckoutForm;

export function buildPlanFromSubscriptionPromoCode2(
  subscriptionPromoCode2: SubscriptionPromoCode2 | null,
  bu: BusinessUser | undefined
): Partial<SubscriptionPlanDTO> | null {
  if (
    !bu ||
    !subscriptionPromoCode2 ||
    !subscriptionPromoCode2.places ||
    !subscriptionPromoCode2.places.length
  ) {
    return null;
  }

  return {
    name: `Custom ${bu.placeName}`,
    isSaas: true,
    price: subscriptionPromoCode2.places[0].basePriceHT,
    type: SubscriptionPlanType.CUSTOM,
    noPrice: false,
    saasFeatures: subscriptionPromoCode2.places[0].saasFeatures,
    includedOptions: (subscriptionPromoCode2.places[0]?.options?.filter(
      (opt) => !!opt.isSaaS && !opt.isDefault
    ) ?? []) as any[]
  };
}
